/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import iconSet from '../../shared/images/teambuildr-selection.json';
import getAllUrlParams from '../../shared/utils/getAllUrlParams';
import Header from './components/presentational/Header';
import CalendarContent from './components/presentational/CalendarContent';

const BANNER_KEY = 'tb_calendar_info_banner_dismissed';

const getInitialBannerDismissed = () => {
  try {
    return localStorage.getItem(BANNER_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

const persistBannerDismissed = () => {
  try {
    localStorage.setItem(BANNER_KEY, 'true');
  } catch (error) {
    // ignore — Safari private mode, blocked iframes, enterprise policy, etc.
  }
};

const PageWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  margin-top: ${(props) => (props.hideNav || props.viewType === 'weekView' ? '0px' : '49px')};
  padding-top: ${(props) => (!props.hideNav && props.viewType === 'weekView' ? '49px' : '')};
  height: ${(props) => (props.hideNav || props.viewType === 'weekView' ? '100%' : 'calc(100% - 49px)')};
  width: 100%;
  background: #F5F5F5;
`;

const IconSideBarAndContentWrapper = styled('div')`
  display: flex;
  position: relative;
  height: ${(props) => {
    if (props.viewType === 'weekView') return '100%';
    if (props.isTvMode) return '100%';
    if (props.headerHeight) return `calc(100% - ${props.headerHeight}px)`;
    return 'calc(100% - 61.5px)';
  }};
  padding-top: ${(props) => (props.viewType === 'weekView' && !props.isTvMode ? `${props.headerHeight}px` : '')};
  ${(props) => props.viewType === 'weekView' && 'min-width: fit-content;'}
`;

const InfoBanner = styled('div')`
  position: fixed;
  top: ${(props) => (props.hideNav ? '87px' : '136px')};
  left: calc(50% + 24px);
  transform: translateX(-50%);
  z-index: 100;
  width: max-content;
  max-width: calc(100vw - 80px);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px 12px 16px;
  background: #FFFBEB;
  border: 2px solid #FFCB47;
  border-radius: 8px;
  color: #333333;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.3px;
  line-height: 1.4;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
`;

const BannerIconWrapper = styled('span')`
  display: inline-flex;
  flex-shrink: 0;

  @media screen and (max-width: 767px) {
    display: none;
  }
`;

const BannerDismiss = styled('button')`
  background: none;
  border: none;
  cursor: pointer;
  color: #1787FB;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 0;
  margin-left: 8px;
  flex-shrink: 0;
  transition: opacity 150ms ease-in-out;
  &:hover {
    opacity: 0.7;
  }
`;

const CalendarLayout = ({ flags }) => {
  const [bannerDismissed, setBannerDismissed] = useState(getInitialBannerDismissed);
  const headerHeight = useSelector((state) => state.calendar.data.headerHeight);
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const isTvMode = useSelector((state) => state.calendar.ui.isTvMode);
  const urlParams = getAllUrlParams(window.location.href, false);
  const hideNav = urlParams.hideNav === 'true' || isTvMode;
  const showBuildBetaBanner = Boolean(flags?.calendarBuildBetaBanner);

  const handleDismissBanner = () => {
    setBannerDismissed(true);
    persistBannerDismissed();
  };

  return (
    <PageWrapper viewType={viewType} hideNav={hideNav}>
      {!isTvMode && <Header />}
      {!isTvMode && showBuildBetaBanner && !bannerDismissed && (
        <InfoBanner hideNav={hideNav}>
          <BannerIconWrapper>
            <IcomoonReact iconSet={iconSet} size={18} icon='warning' color='#F59E0B' />
          </BannerIconWrapper>
          You can build in either Calendar or Build (Beta) — both are &ldquo;live&rdquo; and publish to the same athletes and calendars.
          <BannerDismiss onClick={handleDismissBanner} aria-label='Dismiss banner'>
            Got it
          </BannerDismiss>
        </InfoBanner>
      )}
      <IconSideBarAndContentWrapper
        headerHeight={headerHeight}
        viewType={viewType}
        isTvMode={isTvMode}
      >
        <CalendarContent />
      </IconSideBarAndContentWrapper>
    </PageWrapper>
  );
};

export default withLDConsumer()(CalendarLayout);
